import { useEffect, useState } from "react";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, ChartBar, Money, Ticket } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const CATS = ["concerts", "sports", "festivals", "theater", "university", "conferences"];

export default function OrganizerDashboard() {
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    title: "", description: "", category: "concerts", city: "Dhaka", venue: "", date: "",
    image_url: "", ticket_types: [{ name: "General", price: 500, total: 100, description: "" }],
  };
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/organizer/events").then(({ data }) => setEvents(data));
    api.get("/organizer/analytics").then(({ data }) => setAnalytics(data));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title, description: e.description, category: e.category, city: e.city,
      venue: e.venue, date: e.date.slice(0, 16), image_url: e.image_url,
      ticket_types: e.ticket_types.map(t => ({ name: t.name, price: t.price, total: t.total, description: t.description || "" })),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, ticket_types: form.ticket_types.map(t => ({ ...t, price: parseFloat(t.price), total: parseInt(t.total) })) };
      if (editing) await api.put(`/events/${editing.id}`, payload);
      else await api.post("/events", payload);
      toast.success(editing ? "Event updated" : "Event created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    await api.delete(`/events/${e.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 dark:bg-[#0B0F1A]" data-testid="organizer-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-eyebrow">Organizer</span>
          <h1 className="font-display text-4xl sm:text-6xl mt-2 tracking-tighter dark:text-white">Sell more shows.</h1>
        </div>
        <button onClick={openCreate} className="btn-red inline-flex items-center gap-2" data-testid="create-event-btn">
          <Plus size={18} weight="bold" /> New Event
        </button>
      </div>

      {analytics && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Revenue", v: `৳${analytics.total_revenue.toLocaleString()}`, Icon: Money },
            { l: "Tickets sold", v: analytics.total_tickets, Icon: Ticket },
            { l: "Orders", v: analytics.total_orders, Icon: ChartBar },
            { l: "Active events", v: analytics.total_events, Icon: ChartBar },
          ].map((s, i) => (
            <div key={i} className="stat-card dark:bg-[#111827]">
              <s.Icon size={20} className="text-[#4F46E5]" />
              <p className="font-display text-3xl mt-3 dark:text-white" data-testid={`org-stat-${s.l}`}>{s.v}</p>
              <p className="text-xs uppercase tracking-widest text-neutral-500 dark:text-neutral-400 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="events" className="mt-12">
        <TabsList className="bg-transparent border-b border-neutral-200 dark:border-neutral-700 rounded-none">
          <TabsTrigger value="events" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-org-events">Events</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-org-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="mt-6">
          <div className="table-container dark:bg-[#111827]">
            <table className="w-full text-sm">
              <thead>
                <tr><th>Event</th><th>Date</th><th>Sold / Capacity</th><th>Min price</th><th></th></tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const cap = e.ticket_types.reduce((a, t) => a + t.total, 0);
                  return (
                    <tr key={e.id} data-testid={`org-event-${e.id}`}>
                      <td className="p-3"><div className="font-bold dark:text-white">{e.title}</div><div className="text-xs text-neutral-500 dark:text-neutral-400">{e.venue}</div></td>
                      <td className="p-3 dark:text-neutral-300">{new Date(e.date).toLocaleDateString()}</td>
                      <td className="p-3 dark:text-neutral-300">{e.sold_count || 0} / {cap}</td>
                      <td className="p-3 font-bold dark:text-neutral-300">৳{e.min_price?.toLocaleString()}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => openEdit(e)} className="text-xs font-bold uppercase mr-3 hover:text-[#4F46E5] dark:text-neutral-300" data-testid={`edit-${e.id}`}>Edit</button>
                        <button onClick={() => remove(e)} className="text-neutral-400 hover:text-[#4F46E5]" data-testid={`del-${e.id}`}><Trash size={16} /></button>
                      </td>
                    </tr>
                  );
                })}
                {events.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-neutral-500 dark:text-neutral-400">No events yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          {analytics?.by_event?.length ? (
            <div className="border border-neutral-200 dark:border-neutral-700 p-6 rounded-xl shadow-sm dark:bg-[#151c2c]">
              <h3 className="font-display text-2xl mb-4 dark:text-white">Revenue by event</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={analytics.by_event}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="title" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-neutral-500 dark:text-neutral-400">No data yet.</p>}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl dark:bg-[#151c2c] dark:border-neutral-700">
          <DialogHeader><DialogTitle className="dark:text-white">{editing ? "Edit event" : "Create event"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold dark:text-neutral-300">Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full mt-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-3 py-2 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" data-testid="form-title" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold dark:text-neutral-300">Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full mt-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-3 py-2 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" data-testid="form-desc" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold dark:text-neutral-300">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-3irections py-2 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg">
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold dark:text-neutral-300">Date & Time</label>
              <input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full mt-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-3 py-2 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" data-testid="form-date" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold dark:text-neutral-300">City</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full mt-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-3 py-2 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold dark:text-neutral-300">Venue</label>
              <input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="w-full mt-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-3 py-2 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" data-testid="form-venue" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold dark:text-neutral-300">Image URL</label>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full mt-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-3 py-2 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" placeholder="https://..." />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex justify-between items-center">
              <span className="label-eyebrow dark:text-neutral-400">Ticket types</span>
              <button onClick={() => setForm({ ...form, ticket_types: [...form.ticket_types, { name: "", price: 0, total: 50, description: "" }] })}
                className="text-xs font-bold uppercase hover:text-[#4F46E5] dark:text-neutral-300" data-testid="add-ticket-type">+ Add type</button>
            </div>
            <div className="mt-3 space-y-3">
              {form.ticket_types.map((t, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 border border-neutral-200 dark:border-neutral-700 dark:bg-[#0B0F1A] p-3 rounded-xl">
                  <input placeholder="Name" value={t.name} onChange={(e) => { const tt = [...form.ticket_types]; tt[i].name = e.target.value; setForm({ ...form, ticket_types: tt }); }} className="col-span-3 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-2 py-1.5 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" />
                  <input placeholder="Price" type="number" value={t.price} onChange={(e) => { const tt = [...form.ticket_types]; tt[i].price = e.target.value; setForm({ ...form, ticket_types: tt }); }} className="col-span-2 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-2 py-1.5 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" />
                  <input placeholder="Quantity" type="number" value={t.total} onChange={(e) => { const tt = [...form.ticket_types]; tt[i].total = e.target.value; setForm({ ...form, ticket_types: tt }); }} className="col-span-2 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-2 py-1.5 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" />
                  <input placeholder="Description" value={t.description} onChange={(e) => { const tt = [...form.ticket_types]; tt[i].description = e.target.value; setForm({ ...form, ticket_types: tt }); }} className="col-span-4 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-2 py-1.5 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" />
                  <button onClick={() => setForm({ ...form, ticket_types: form.ticket_types.filter((_, x) => x !== i) })} className="col-span-1 text-neutral-400 hover:text-[#4F46E5] flex items-center justify-center"><Trash size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="dark:text-white">
            <button className="btn-outline dark:border-neutral-600" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-red" onClick={save} data-testid="form-save">{editing ? "Save changes" : "Publish event"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}